import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FiatWalletAccountState } from '#src/__generated__/globalTypes.ts';
import { ButtonBase } from '#src/atoms/buttons/ButtonBase/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { CreateFiatWallet } from '#src/components/fiatWallet/CreateFiatWallet/index.tsx';
import { useCashWalletKycEvents } from '#src/hooks/wallets/useCashWalletKycEvents.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { fiatWallet } from '#src/lib/glossary.ts';
import type { EnterCashWalletKYCFlow_Source } from '#src/protos/events/platform/web/events.ts';
import { EnterCashWalletKYCFlow_Target } from '#src/protos/events/platform/web/events.ts';

const WithPadding = styled.div`
  padding: 0 var(--double-unit) var(--double-unit);
`;

const Helper = styled(Vertical)`
  text-align: left;
  padding: var(--intermediate-unit);
  background-color: var(--c-nd-200);
  border-radius: var(--unit);
  width: 100%;
`;

const StyledButtonBase = styled(ButtonBase)`
  color: var(--c-link);
  font-weight: var(--t-bold);
  text-align: left;
`;

type Props = {
  statusTarget: FiatWalletAccountState;
  defaultHelper: ReactNode;
  canListAndTradeHelper?: ReactNode;
  withPadding?: boolean;
  source: EnterCashWalletKYCFlow_Source;
};

export const NeedsValidatedFiatWalletHelper = ({
  canListAndTradeHelper,
  defaultHelper,
  statusTarget = FiatWalletAccountState.VALIDATED_OWNER,
  withPadding = false,
  source,
}: Props) => {
  const [showCreateFiatWallet, setShowCreateFiatWallet] = useState(false);
  const { trackEnter } = useCashWalletKycEvents();
  const { canListAndTrade, canDepositAndWithdraw } = useFiatBalance();
  const cta = useMemo(() => {
    if (statusTarget === FiatWalletAccountState.OWNER)
      return <FormattedMessage {...fiatWallet.activateCashWallet} />;
    return (
      <FormattedMessage
        {...(canListAndTrade
          ? fiatWallet.addMyId
          : fiatWallet.activateCashWalletAndAddAnId)}
      />
    );
  }, [canListAndTrade, statusTarget]);

  if (canDepositAndWithdraw) return null;

  if (statusTarget === FiatWalletAccountState.OWNER && canListAndTrade)
    return null;

  const helper = (
    <>
      <Helper>
        <LabelM color="var(--c-nd-700)">
          {canListAndTrade ? canListAndTradeHelper : defaultHelper}
        </LabelM>
        <LabelM bold>
          <StyledButtonBase
            onClick={() => {
              setShowCreateFiatWallet(true);
              trackEnter({
                source,
                target:
                  statusTarget === FiatWalletAccountState.VALIDATED_OWNER
                    ? EnterCashWalletKYCFlow_Target.VALIDATED_OWNER
                    : EnterCashWalletKYCFlow_Target.OWNER,
              });
            }}
          >
            {cta}
          </StyledButtonBase>
        </LabelM>
      </Helper>
      {!!showCreateFiatWallet && (
        <CreateFiatWallet
          onDismissActivationSuccess={() => setShowCreateFiatWallet(false)}
          onClose={() => setShowCreateFiatWallet(false)}
          statusTarget={statusTarget}
          canDismissAfterActivation={false}
        />
      )}
    </>
  );
  if (withPadding) return <WithPadding>{helper}</WithPadding>;
  return helper;
};
